import React, { useMemo, useState } from 'react';
import { Modal, Select, Button, Flexbox, Typography, Box } from '@hiiretail/synergy-ui';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { useIntl } from 'react-intl';
import { useGetResources } from '../../../../hooks/useGetResources';
import { Spinner } from '../../../../../../common/components/Spinner/Spinner';
import { ResourceDto } from '../../../../../../api-client';
import { I18nKeys } from '../../../../../../i18n/keys';
import styled from 'styled-components';

const ResourceSelect = styled(Select)`
  height: 400px;
`;

type AddFixedBindingModalProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (bindings: string[]) => void;
  isAdding: boolean;
  existingBindings: string[];
};

export const AddFixedBindingModal: React.FC<AddFixedBindingModalProps> = ({
  open,
  onClose,
  onAdd,
  isAdding,
  existingBindings = [],
}) => {
  const [selectedResources, setSelectedResources] = useState<Option[]>([]);

  const { data: resources, isLoading: isLoadingResources } = useGetResources({
    enabled: open,
  });

  const resourceOptions = useMemo(() => {
    if (!resources) return [];
    return resources
      .filter((resource: ResourceDto) => !existingBindings.includes(resource.id))
      .map((resource: ResourceDto) => ({
        label: `${resource.id} - ${resource.name}`,
        value: resource.id,
      }));
  }, [resources, existingBindings]);

  const handleAdd = () => {
    const bindingsToAdd = selectedResources.map((option) => option.value as string);
    onAdd(bindingsToAdd);
    setSelectedResources([]);
  };

  const handleClose = () => {
    setSelectedResources([]);
    onClose();
  };

  const intl = useIntl();

  return (
    <Modal
      data-testid="add-fixed-binding-modal"
      open={open}
      onClose={handleClose}
      title={intl.formatMessage({ id: I18nKeys.CustomRolesAddFixedBindings })}
      footer={
        <Flexbox container justifyContent="flex-end" gutter={2}>
          <Button variant="secondary" onClick={handleClose} disabled={isAdding}>
            {intl.formatMessage({ id: I18nKeys.Cancel, defaultMessage: 'Cancel' })}
          </Button>
          <Button onClick={handleAdd} disabled={isAdding || selectedResources.length === 0}>
            {intl.formatMessage({ id: I18nKeys.Add, defaultMessage: 'Add' })}
          </Button>
        </Flexbox>
      }
    >
      <Flexbox container direction="column" gutter={3}>
        <Typography variant="body1">
          {intl.formatMessage({ id: I18nKeys.CustomRolesFixedBindingsDescription })}
        </Typography>
        {isLoadingResources ? (
          <Box
            style={{
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </Box>
        ) : (
          <ResourceSelect
            fullWidth
            isMulti
            name="resources"
            label={intl.formatMessage({ id: I18nKeys.CustomRolesSelectResources })}
            value={selectedResources}
            onChange={(value) => setSelectedResources(value)}
            options={resourceOptions}
            placeholder={intl.formatMessage({ id: I18nKeys.CustomRolesSelectResourcesPlaceholder })}
            disabled={isAdding}
            isClearable={false}
          />
        )}
      </Flexbox>
    </Modal>
  );
};
